/**
 * RowFooterSlot - Renders controls in the row-footer location.
 *
 * This slot appears after each row (full width) and could be used for
 * row-level controls or metadata.
 */
import {type MediaCardType} from '@wandb/weave/common/types/media';
import React from 'react';

import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../../Component';
import {type StepMeta} from '../../types';
import {ControlRenderer} from '../renderers/ControlRenderer';
import {type ControlDefinition, type GridCell, type GridData} from '../types';

export type ColumnFooterSlotProps = {
  controls: ControlDefinition[];
  columnIndex: number;
  cell?: GridCell;
  disableRunLinks?: boolean;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  mediaSliderKey: string;
  mediaType: MediaCardType;
  isMaxStepLoading: boolean;
  mediaBrowserSize: {width: number; height: number};
  gridData: GridData;
};

/**
 * Renders controls in the row-footer location.
 *
 * All controls are rendered through ControlRenderer, which delegates to the
 * appropriate control component that handles its own rendering.
 *
 * @param props - Row footer slot props
 * @returns Rendered row footer or null if no controls
 */
export const ColumnFooterSlot: React.FC<ColumnFooterSlotProps> = ({
  controls,
  config,
  updateConfig,
  stepMeta,
  columnIndex,
}) => {
  if (controls.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center justify-center gap-4 h-full">
      {controls.map((control, idx) => (
        <ControlRenderer
          // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
          key={`row-footer-control-${idx}`}
          control={control}
          index={idx}
          config={config}
          updateConfig={updateConfig}
          stepMeta={stepMeta}
          columnIndex={columnIndex}
        />
      ))}
    </div>
  );
};
